import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { Checkbox, Skeleton, Table, Tooltip } from 'antd';
import moment from 'moment';
import { AssetItemDto } from '@api/Api';
import UserAvatar from '@components/UserAvatar';
import FolderActions from '@components/Tables/FolderActions';
import { ReorderEvent } from '@components/DraggableList/DraggableList';
import { FolderIcon, FolderPrivateIcon } from '@icons';
import { showReorderRequiresCustomSortHint } from '@helpers/showReorderRequiresCustomSortHint';
import './Table.less';

function AssetsListSkeleton() {
  return (
    <div className="assets_card_container" style={{ height: 56 }}>
      <div className="assets_card_container__preview">
        <Skeleton.Input style={{ width: '100%', height: 56 }} />
      </div>
    </div>
  );
}

function FoldersTable({
  foldersList,
  onDrop,
  onFolderDrop,
  onReorder,
  canReorganize,
  countToFetch,
  listLoading,
  canShare,
  selectedFolders = [],
  setSelectedFolders,
  foldersCount = 0
}: {
  foldersList: {
    node: AssetItemDto;
    cursor: string;
  }[];
  onDrop?: (assetId: string, folderId: string) => void;
  onFolderDrop?: (sourceFolderId: string, targetFolderId: string) => void;
  onReorder?: (e: ReorderEvent) => void;
  canReorganize?: boolean;
  countToFetch: number;
  listLoading: boolean | undefined;
  canShare?: boolean;
  selectedFolders?: {
    node: AssetItemDto;
    cursor: string;
  }[];
  setSelectedFolders?: (
    ids: string[],
    folders: { node: AssetItemDto; cursor: string }[],
    isAllSelect: boolean
  ) => void;
  foldersCount?: number;
}) {
  const navigate = useNavigate();
  const dragIndexRef = useRef<number | null>(null);
  const draggingIdRef = useRef<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [overState, setOverState] = useState<{
    id: string;
    position: 'top' | 'center' | 'bottom';
  } | null>(null);
  const showCheckbox = !!setSelectedFolders;

  const getDropPosition = (y: number, height: number) => {
    const topBoundary = height * 0.33;
    const bottomBoundary = height * 0.67;
    if (y < topBoundary) return 'top' as const;
    if (y > bottomBoundary) return 'bottom' as const;
    return 'center' as const;
  };

  const getDragPayload = (e: React.DragEvent) => {
    const transferTypes = Array.from(e.dataTransfer.types);
    let type: string | undefined;
    if (transferTypes.includes('streamwork://type/asset')) {
      type = 'asset';
    } else if (transferTypes.includes('streamwork://type/folder')) {
      type = 'folder';
    } else if (transferTypes.includes('streamwork://type')) {
      type = e.dataTransfer.getData('streamwork://type') || undefined;
    } else if (draggingIdRef.current) {
      type = 'folder';
    }

    const idByToken = transferTypes
      .find((x) => x.startsWith('streamwork://id/'))
      ?.split('/')
      .slice(-1)[0];
    const id =
      idByToken ||
      (transferTypes.includes('streamwork://id')
        ? e.dataTransfer.getData('streamwork://id')
        : undefined) ||
      draggingIdRef.current ||
      '';

    return { type, id };
  };

  if (!listLoading && !foldersList.length) return null;
  if (listLoading && !countToFetch) return null;

  const columns = [
    {
      title: (
        <div className="table_name_header">
          {showCheckbox && (
            <Checkbox
              checked={
                !!selectedFolders.length &&
                selectedFolders.length === foldersCount
              }
              indeterminate={
                !!selectedFolders.length &&
                selectedFolders.length < foldersCount
              }
              onClick={(e) => e.stopPropagation()}
              onChange={() => {
                if (!setSelectedFolders) return;
                if (selectedFolders.length) {
                  setSelectedFolders([], [], true);
                  return;
                }
                setSelectedFolders(
                  foldersList.map((x) => x.node.id),
                  foldersList,
                  true
                );
              }}
            />
          )}
          Name
        </div>
      ),
      dataIndex: 'name',
      key: 'name',
      width: 480,
      render: (text: string, { node }: { node: AssetItemDto }) => {
        const count = node.folder?.totalAssetsCount || 0;
        let copy = 'asset';
        if (count === 0 || count > 1) copy += 's';
        const assetsCountCopy = `${count} ${copy}`;

        return (
          <div className="folder_record">
            {showCheckbox && (
              <Checkbox
                checked={selectedFolders.some((x) => x.node.id === node.id)}
                className="checkbox_selected"
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  if (!setSelectedFolders) return;
                  const folder = foldersList.find((x) => x.node.id === node.id);
                  const next = e.target.checked
                    ? folder
                      ? [...selectedFolders, folder]
                      : selectedFolders
                    : selectedFolders.filter((x) => x.node.id !== node.id);
                  setSelectedFolders(
                    next.map((x) => x.node.id),
                    next,
                    false
                  );
                }}
              />
            )}
            <div className="folder_record__type">
              {node.isPrivate ? <FolderPrivateIcon /> : <FolderIcon />}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Tooltip
                title={node.folder?.name}
                overlayClassName="toggle_favorite_overlay toggle_favorite_overlay--small"
                placement="topLeft"
              >
                <div className="folder_record__name">{node.folder?.name}</div>
              </Tooltip>
              <div className="folder_record__assets">{assetsCountCopy}</div>
            </div>
          </div>
        );
      }
    },
    {
      title: 'Owner',
      dataIndex: 'owner',
      key: 'owner',
      width: 190,
      render: (text: string, { node }: { node: AssetItemDto }) => {
        return (
          <div className="user_record">
            <div className="user_record__avatar">
              <UserAvatar
                isActive={true}
                size={40}
                src={node.owner?.user?.picture.url || ''}
                userEmail={node.owner?.user?.email || ''}
                userName={node.owner?.user?.name || ''}
              />
            </div>
          </div>
        );
      }
    },
    {
      title: 'Created',
      dataIndex: 'created',
      key: 'created',
      width: 190,
      render: (text: string, { node }: { node: AssetItemDto }) => {
        return (
          <span className="time-state">
            {moment(node.createdAt).format('MMM DD, YYYY')}
          </span>
        );
      }
    },
    {
      title: 'Modified',
      key: 'modified',
      dataIndex: 'modified',
      width: 160,
      render: (text: string, { node }: { node: AssetItemDto }) => {
        return (
          <span className="time-state">
            {moment(node.modifiedAt).fromNow()}
          </span>
        );
      }
    },
    {
      title: '',
      key: 'action',
      render: (text: string, { node }: { node: AssetItemDto }) => (
        <FolderActions node={node} canShare={canShare} />
      )
    }
  ];
  return (
    <div
      className={classNames('table_container', {
        'table_container--dragging': !!draggingId
      })}
    >
      <Table
        locale={{
          emptyText: new Array(countToFetch).fill('').map((_, idx: any) => (
            <React.Fragment key={idx}>
              <AssetsListSkeleton /> <br />
            </React.Fragment>
          ))
        }}
        pagination={false}
        columns={columns}
        dataSource={foldersList}
        rowKey={({ node }) => node.id}
        rowClassName={({ node }) =>
          classNames({
            'table-row--checked': selectedFolders.some(
              (x) => x.node.id === node.id
            ),
            'table-row--dragging': draggingId === node.id,
            'table-row--over-top':
              overState?.id === node.id && overState?.position === 'top',
            'table-row--over-center':
              overState?.id === node.id && overState?.position === 'center',
            'table-row--over-bottom':
              overState?.id === node.id && overState?.position === 'bottom'
          })
        }
        onRow={({ node }, rowIndex) => ({
          'data-marquee-id': node.id,
          'data-marquee-type': 'folder',
          draggable: true,
          onClick: () => {
            if (node.deletedAt) return;
            navigate(`/campaigns/single/${node.campaignId}/${node.id}`);
          },
          onDragStart: (e) => {
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('streamwork://id', node.id);
            e.dataTransfer.setData(`streamwork://id/${node.id}`, node.id);
            e.dataTransfer.setData('streamwork://type', 'folder');
            e.dataTransfer.setData('streamwork://type/folder', 'folder');
            dragIndexRef.current = rowIndex ?? null;
            draggingIdRef.current = node.id;
            setDraggingId(node.id);

            const rowEl = e.currentTarget as HTMLElement;
            const folderIcon = rowEl.querySelector('.folder_record__type svg');
            const folderName = rowEl.querySelector(
              '.folder_record__name'
            )?.textContent;
            if (!folderIcon || !folderName) return;

            const dragImageContainer = document.createElement('div');
            const dragIcon = folderIcon.cloneNode(true) as SVGElement;
            const dragTitle = document.createElement('div');
            dragTitle.textContent = folderName;
            dragImageContainer.classList.add('drag-image');
            dragImageContainer.classList.add('dragged_folder_container');
            document.body.appendChild(dragImageContainer);
            dragImageContainer.appendChild(dragIcon);
            dragImageContainer.appendChild(dragTitle);
            e.dataTransfer.setDragImage(dragImageContainer, 0, 0);
            setTimeout(() => {
              dragImageContainer.style.transform = 'scale(1)';
            }, 1);
          },
          onDrag: (e) => {
            const preview = document.querySelector(
              '.drag-image'
            ) as HTMLElement | null;
            if (!preview) return;
            if (e.pageY || e.pageX) {
              preview.style.top = `${e.pageY + 10}px`;
              preview.style.left = `${e.pageX + 10}px`;
            }

            const shouldShrink = overState?.position === 'center';
            preview.style.transform = shouldShrink ? 'scale(0)' : 'scale(1)';
          },
          onDragEnd: () => {
            const preview = document.querySelector(
              '.drag-image'
            ) as HTMLElement | null;
            if (preview) {
              preview.remove();
            }
            setDraggingId(null);
            setOverState(null);
            dragIndexRef.current = null;
            draggingIdRef.current = null;
          },
          onDragOver: (e) => {
            const { type, id } = getDragPayload(e);
            if (type === 'asset') {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              setOverState({ id: node.id, position: 'center' });
              const preview = document.querySelector(
                '.drag-image'
              ) as HTMLElement | null;
              if (preview) {
                preview.style.transform = 'scale(0)';
              }
            }
            if (type === 'folder' && id !== node.id) {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              const rect = (
                e.currentTarget as HTMLElement
              ).getBoundingClientRect();
              const y = e.clientY - rect.top;
              const position = getDropPosition(y, rect.height);
              setOverState({ id: node.id, position });
              const preview = document.querySelector(
                '.drag-image'
              ) as HTMLElement | null;
              if (preview) {
                preview.style.transform =
                  position === 'center' ? 'scale(0)' : 'scale(1)';
              }
            }
          },
          onDragLeave: () => {
            const preview = document.querySelector(
              '.drag-image'
            ) as HTMLElement | null;
            if (preview) {
              preview.style.transform = 'scale(1)';
            }
            setOverState(null);
          },
          onDrop: (e) => {
            e.preventDefault();
            const { type, id } = getDragPayload(e);
            const sourceIndex = dragIndexRef.current;
            setOverState(null);
            const preview = document.querySelector(
              '.drag-image'
            ) as HTMLElement | null;
            if (preview) {
              preview.remove();
            }
            setDraggingId(null);
            draggingIdRef.current = null;
            if (type === 'asset' && onDrop) {
              onDrop(id, node.id);
            }
            if (type === 'folder' && id && id !== node.id) {
              if (onReorder && sourceIndex !== null && rowIndex !== undefined) {
                const rect = (
                  e.currentTarget as HTMLElement
                ).getBoundingClientRect();
                const y = e.clientY - rect.top;
                const position = getDropPosition(y, rect.height);
                if (position !== 'center') {
                  if (!canReorganize) {
                    showReorderRequiresCustomSortHint();
                    dragIndexRef.current = null;
                    return;
                  }
                  let targetIndex = rowIndex;
                  if (position === 'top') {
                    targetIndex =
                      rowIndex > sourceIndex ? rowIndex - 1 : rowIndex;
                  } else {
                    targetIndex =
                      rowIndex > sourceIndex ? rowIndex : rowIndex + 1;
                  }

                  if (targetIndex !== sourceIndex) {
                    const boundedTargetIndex = Math.max(
                      0,
                      Math.min(targetIndex, foldersList.length - 1)
                    );
                    onReorder({
                      item: { id, type: 'folder' },
                      prevIndex: sourceIndex,
                      newIndex: boundedTargetIndex
                    });
                  }
                  dragIndexRef.current = null;
                  return;
                }
              }
              if (onFolderDrop) {
                onFolderDrop(id, node.id);
              }
            }
            dragIndexRef.current = null;
          }
        })}
      />

      {new Array(countToFetch).fill('').map((_, idx) => (
        <div key={idx} className="skeleton">
          <Skeleton.Input
            style={{ width: '100%', height: 56, marginBottom: 27 }}
          />{' '}
          <br />{' '}
        </div>
      ))}
    </div>
  );
}

export default FoldersTable;
